import { ANNOTATION_TOOLS } from "./constants";
import {
  EditRounded as AnnotateIcon,
  DragIndicatorRounded as DragIcon,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

const DraggableAnnotationTool = ({ tool }) => {
  const theme = useTheme();
  const Icon = tool.icon;

  // Skip the "select" tool - it's not draggable
  if (tool.id === "select") {
    return null;
  }

  const handleDragStart = (e) => {
    e.dataTransfer.setData(
      "application/annotation",
      JSON.stringify({ type: tool.id })
    );
    e.dataTransfer.effectAllowed = "copy";
  };

  return (
    <Tooltip
      title={
        <Box>
          <Typography variant={"caption"} sx={{ fontWeight: 600 }}>
            {tool.label}
          </Typography>
          <Typography
            variant={"caption"}
            display={"block"}
            sx={{ opacity: 0.8 }}
          >
            {"Drag onto document"}
          </Typography>
        </Box>
      }
      placement={"left"}
      arrow
    >
      <Box
        draggable
        onDragStart={handleDragStart}
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "transparent",
          border: `2px dashed ${theme.palette.divider}`,
          color: tool.color,
          cursor: "grab",
          transition: "all 0.15s ease",
          position: "relative",
          "&:hover": {
            backgroundColor: `${tool.color}15`,
            borderColor: tool.color,
            borderStyle: "solid",
            transform: "scale(1.05)",
          },
          "&:active": {
            cursor: "grabbing",
            transform: "scale(0.95)",
          },
        }}
      >
        <Icon fontSize={"small"} />
        <DragIcon
          sx={{
            position: "absolute",
            bottom: 2,
            right: 2,
            fontSize: 10,
            opacity: 0.5,
          }}
        />
      </Box>
    </Tooltip>
  );
};

const AnnotationToolbar = ({ annotations }) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        width: 64,
        height: "100%",
        borderLeft: `1px solid ${theme.palette.divider}`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Stack alignItems={"center"}>
          <AnnotateIcon fontSize={"small"} color={"action"} />
          <Typography
            variant={"caption"}
            sx={{ fontSize: "0.65rem", color: "text.secondary", mt: 0.5 }}
          >
            {"Annotate"}
          </Typography>
        </Stack>
      </Box>

      <Box sx={{ p: 1, flex: 1 }}>
        <Typography
          variant={"caption"}
          sx={{
            fontSize: "0.6rem",
            color: "text.secondary",
            display: "block",
            textAlign: "center",
            mb: 1,
          }}
        >
          {"Drag to add"}
        </Typography>
        <Stack spacing={0.5}>
          {ANNOTATION_TOOLS.filter((t) => t.id !== "select").map((tool) => (
            <DraggableAnnotationTool key={tool.id} tool={tool} />
          ))}
        </Stack>
      </Box>

      {annotations.length > 0 && (
        <Box sx={{ p: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Stack alignItems={"center"}>
            <Chip
              label={annotations.length}
              size={"small"}
              color={"primary"}
              sx={{ height: 20, fontSize: "0.65rem" }}
            />
            <Typography
              variant={"caption"}
              sx={{
                fontSize: "0.6rem",
                color: "text.secondary",
                mt: 0.5,
                textAlign: "center",
              }}
            >
              {"marks"}
            </Typography>
          </Stack>
        </Box>
      )}
    </Paper>
  );
};

export default AnnotationToolbar;
